import { DisplayObject, Rectangle } from "pixi.js"
import { TEXT_ROLES } from "../text/typography"
import { stageViewport } from "../../../../canvas_layout"
import { isMobile, isPhone } from "../../../../client_util"

export const TOUCH_TARGET_CSS = 44
export interface UiViewport { left: number; top: number; width: number; height: number }
export interface UiInsets { left: number; right: number; top: number; bottom: number }
export interface UiMetricsInput {
    viewport: UiViewport
    cssScaleX: number
    cssScaleY: number
    touch: boolean
    phone?: boolean
    safeInsets?: UiInsets
}

export const resolveUiMetrics = ({ viewport, cssScaleX, cssScaleY, touch, phone = isPhone(), safeInsets }: UiMetricsInput) => {
    const scaleX = cssScaleX > 0 ? cssScaleX : 1
    const scaleY = cssScaleY > 0 ? cssScaleY : 1
    const inset = safeInsets ?? { left: 0, right: 0, top: 0, bottom: 0 }
    const left = Math.min(viewport.width, Math.max(0, inset.left) / scaleX)
    const top = Math.min(viewport.height, Math.max(0, inset.top) / scaleY)
    const width = Math.max(0, viewport.width - left - Math.max(0, inset.right) / scaleX)
    const height = Math.max(0, viewport.height - top - Math.max(0, inset.bottom) / scaleY)
    return {
        viewport, scaleX, scaleY, touch, phone,
        density: width * scaleX < 700 || height * scaleY < 400 ? "compact" as const : "regular" as const,
        safeViewport: { left: viewport.left + left, top: viewport.top + top, width, height,
            right: viewport.left + left + width, bottom: viewport.top + top + height },
        touchWidth: touch ? TOUCH_TARGET_CSS / scaleX : 0,
        touchHeight: touch ? TOUCH_TARGET_CSS / scaleY : 0,
    }
}

export const hasTouchInput = () => typeof navigator !== "undefined" && (
    navigator.maxTouchPoints > 0 || (typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia("(any-pointer: coarse)").matches : isMobile())
)

let current = resolveUiMetrics({ viewport: stageViewport(), cssScaleX: 1, cssScaleY: 1, touch: hasTouchInput() })
let rendererScaleX = 1
let rendererScaleY = 1
export const getUiMetrics = () => current
export const updateUiMetrics = (input: UiMetricsInput, stageScaleY = 1) => {
    current = resolveUiMetrics(input)
    rendererScaleX = current.scaleX
    rendererScaleY = current.scaleY / stageScaleY
}

// Account for nested panel scaling as well as the stage and canvas CSS transforms.
export const controlCssScale = (control: DisplayObject) => {
    if (!control.parent) return { x: current.scaleX * Math.abs(control.scale.x) || 1, y: current.scaleY * Math.abs(control.scale.y) || 1 }
    control.toGlobal({ x: 0, y: 0 })
    const m = control.worldTransform
    return { x: Math.hypot(m.a * rendererScaleX, m.b * rendererScaleY) || 1,
        y: Math.hypot(m.c * rendererScaleX, m.d * rendererScaleY) || 1 }
}

export const controlHitRect = (control: DisplayObject, width: number, height: number, maxPadding: number | { x: number; y: number } = Infinity) => {
    const scale = controlCssScale(control)
    const limitX = typeof maxPadding === "number" ? maxPadding : maxPadding.x
    const limitY = typeof maxPadding === "number" ? maxPadding : maxPadding.y
    const padX = current.touch ? Math.min(limitX, Math.max(0, (TOUCH_TARGET_CSS / scale.x - width) / 2)) : 0
    const padY = current.touch ? Math.min(limitY, Math.max(0, (TOUCH_TARGET_CSS / scale.y - height) / 2)) : 0
    return new Rectangle(-padX, -padY, width + padX * 2, height + padY * 2)
}

// Recalculate when hit-testing too: a parent can be rescaled without a viewport resize event.
export class ResponsiveHitRect extends Rectangle {
    constructor(private measure: () => Rectangle) {
        super()
        this.refresh()
    }
    refresh = () => { this.copyFrom(this.measure()) }
    contains(x: number, y: number): boolean {
        this.refresh()
        return super.contains(x, y)
    }
}

export const syncUiMetrics = (canvas: HTMLCanvasElement, virtualWidth: number, virtualHeight: number, stageScaleY: number) => {
    const bounds = canvas.getBoundingClientRect()
    const css = getComputedStyle(document.documentElement)
    const safe = (side: string) => parseFloat(css.getPropertyValue(`--ui-safe-${side}`)) || 0
    updateUiMetrics({ viewport: stageViewport(), touch: hasTouchInput(),
        cssScaleX: bounds.width / virtualWidth, cssScaleY: bounds.height / virtualHeight * stageScaleY,
        safeInsets: {
            left: Math.max(0, safe("left") - bounds.left),
            right: Math.max(0, safe("right") - (window.innerWidth - bounds.right)),
            top: Math.max(0, safe("top") - bounds.top),
            bottom: Math.max(0, safe("bottom") - (window.innerHeight - bounds.bottom)),
        },
    }, stageScaleY)
}

export const chipMetrics = () => {
    const scale = Math.min(1, current.scaleX, current.scaleY)
    return current.phone
        ? { padX: 10, padY: 8, glyphSize: Math.max(TEXT_ROLES.title.fontSize, TEXT_ROLES.compactLabel.fontSize / scale), labelSize: Math.max(TEXT_ROLES.body.fontSize, TEXT_ROLES.compactCaption.fontSize / scale),
            gap: 6, minWidth: Math.max(52, current.touchWidth), minHeight: Math.max(52, current.touchHeight), hitPad: 3, corner: 8, rowGap: 8 }
        : { padX: 6, padY: 4, glyphSize: TEXT_ROLES.compactLabel.fontSize, labelSize: TEXT_ROLES.overline.fontSize, gap: 4, minWidth: 0, minHeight: 0, hitPad: 3, corner: 5, rowGap: 5 }
}
export const closeControlRadius = () => current.touch ? 17 : 11
